<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t('new_role') }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/roles" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <form @submit.prevent="saveRole" enctype="multipart/form-data">
      <div class="card">
        <div class="card-body add-product pb-0">
          <div class="accordion-card-one accordion" id="accordionExample">
            <div class="accordion-item">
              <div id="collapseOne" class="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionExample">
                <div class="accordion-body">
                  <div class="row">
                    <div class="col-lg-6 col-md-6 mb-3">
                      <label class="form-label">{{$t('role_name')}} <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        v-model="form.name"
                        class="form-control"
                        :placeholder="$t('enter_role_name')"
                      />
                      <div v-if="errors.name" class="text-danger mt-1">{{ errors.name }}</div>
                    </div>

                    <div class="col-lg-6 col-md-6 mb-3">
                      <label class="form-label">{{$t('description')}} </label>
                      <input
                        type="text"
                        v-model="form.description"
                        class="form-control"
                        :placeholder="$t('enter_description')"
                      />
                      <div v-if="errors.description" class="text-danger mt-1">{{ errors.description }}</div>
                    </div>

                    <div class="col-md-6">
                      <div class="d-flex align-items-center">
                        <label class="form-label small mb-0 me-3" style="width: 90px;" >{{ $t('user_type') }} <span class="text-danger">*</span></label>
                        <VueSelect2
                          v-model="form.user_type"
                          :options="formattedusertypes"
                          :settings="{
                            placeholder: $t('select_an_option'),
                            allowClear: true,
                            tags: true,
                          }"
                          @change="onChange"
                          class="form-control"
                        />
                      </div>
                      <div class="text-danger mt-1" style="height:0.75em; margin-left: 150px; font-size:0.875rem;">
                        <span v-if="errors.user_type">{{ errors.user_type }}</span>
                      </div>
                    </div>

                    <permission-tree
                      :categories="categories"
                      :selected="selectedPerms"
                      :user-type-permissions="userTypePermissions"
                      :user-type="userTypeKey"
                      @update="updatePermission"
                    />

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-12 text-end">
        <div class="btn-addproduct mb-4">
          <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
          <button type="submit" class="btn btn-submit" @click="onSubmit">{{ $t('submit') }}</button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import roleApi from "@/api/roles";
import permApi from "@/api/permissions";
import PermissionTree from "@/components/permissions/PermissionTree.vue";
import VueSelect2 from "vue3-select2-component";
import {useToast} from "vue-toastification";
import Vue3Datepicker from "vue3-datepicker";

export default {
  components: {
    PermissionTree,
    Vue3Datepicker,
    VueSelect2,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      form: {
        name: "",
        user_type: null,
        description: "",
        order: 0,
        role_permissions: []
      },
      userTypes: [],
      categories: [],
      selectedPerms: {},
      userTypePermissions: [],

      errors: {},
      loading: false,
    };
  },

  watch: {
    async 'form.user_type'(newVal) {
      this.selectedPerms = {};

      if (!newVal) {
        this.userTypePermissions = [];
        return;
      }

      const res = await this.$axios.get(
        `/user/permissions/user-type/${newVal}/`
      );

      this.userTypePermissions = res.data;

    }
  },

  async created() {
    // Load User Types
    const ut = await this.$axios.get("/user/user-types/");
    this.userTypes = ut.data;

    // Load Permission Categories
    const pc = await permApi.categories();
    this.categories = pc.data;
  },

  computed: {
    formattedusertypes() {
      return this.userTypes.map(user_type => ({ id: user_type.id, text: user_type.name }));
    },

    userTypeKey() {
      if (!this.form.user_type) return null;

      // Handle VueSelect2 value: could be array or object
      let selectedId = null;

      if (Array.isArray(this.form.user_type)) {
        // If somehow multiple, pick first
        selectedId = this.form.user_type[0]?.id || null;
      } else if (typeof this.form.user_type === 'object') {
        selectedId = this.form.user_type.id;
      } else {
        selectedId = this.form.user_type;
      }

      const ut = this.userTypes.find(t => t.id == selectedId);

      return ut ? ut.key.toLowerCase() : null;
    }
  },

  methods: {
    updatePermission({ id, level }) {
      const permId = Number(id);
      this.selectedPerms[permId] = level;
    },

    async saveRole() {
      this.loading = true;
      this.errors = {};

      if (!this.form.name.trim()) this.errors.name = this.$t('error.required');
      if (!this.form.user_type) this.errors.user_type = this.$t('error.required');

      if (Object.keys(this.errors).length > 0) {
        return;
      }

      this.form.role_permissions = Object.entries(this.selectedPerms)
        .filter(([permission_item_id, access_level]) => {
          return permission_item_id != null && permission_item_id !== "" && access_level != null;
        })
        .map(([permission_item_id, access_level]) => ({
          permission_item_id: Number(permission_item_id),
          access_level: access_level || "N"
        }));

      try {
        await roleApi.create(this.form);
        this.toast.success('Role successfully created!')

        this.resetForm();
        this.$router.push("/roles");

      } catch (error) {
        if (error.response) {
          const status = error.response.status;

          if (status === 400) {
            this.errors = error.response.data || {};
          }
          else if (status === 401 || status === 403) {
            alert(this.$t("permission_denied"));
          }
          else {
            alert(this.$t("something_went_wrong"));
          }
        } else {
          alert(this.$t("network_error"));
        }
      } finally {
        this.loading = false;
      }
    },

    cancelForm() {
      this.$router.push('/roles');
    },

    resetForm() {
      this.form = {
        name: "",
        user_type: null,
        description: ''
      };
      this.errors = {};
    },
  }
};
</script>
